import React, { FC, memo, useEffect, useRef, useState } from 'react';

import { ReactComponent as LocationIcon } from 'applicaiton/assets/location.svg';
import { fetchLocations } from 'applicaiton/store/reducers/MapBox/ActionCreators';
import { getUserCountryIso } from 'applicaiton/sessionStorage/auth';
import { resetSuggestions } from 'applicaiton/store/reducers/MapBox/MapBoxSlice';

import { Context } from 'common/types/mapBox';
import { useDebounce } from 'common/hooks/useDebounce';
import useOnClickOutside from 'common/hooks/useClickOutside';
import { useAppDispatch, useAppSelector } from 'common/hooks/redux';

import Input from '../Input';

import { MapBoxStyled, LocationItem } from './styles';

interface MapBoxProps {
  id: string;
  defaultValue: string;
  setLocation: (location: Context) => void;
  label?: string;
  showRequiredIcon?: boolean;
  errorMessage?: string;
  countryIso?: string;
}

/**
 * React component for an autocomplete input field with suggestions based on location.
 * @param {MapBoxProps} props - The props for the MapBoxAutocompleteInput component.
 * @returns A React component that renders an autocomplete input field.
 */

const MapBoxAutocompleteInput: FC<MapBoxProps> = ({
  id,
  defaultValue,
  setLocation,
  label,
  showRequiredIcon,
  errorMessage,
  countryIso = '',
}) => {
  const dispatch = useAppDispatch();
  const ref = useRef(null);
  const { suggestions } = useAppSelector((state) => state.mapBox);
  const [searchItem, setSearchItem] = useState(defaultValue);
  const [showList, setShowList] = useState(false);
  const [country, setCountry] = useState<string>(
    countryIso || getUserCountryIso() || '',
  );
  const searchQuery = useDebounce(
    searchItem !== defaultValue ? searchItem : '',
    500,
  );

  useOnClickOutside(ref, () => {
    setShowList(false);
    setSearchItem(defaultValue);
  });

  /**
   * Resets the search query and country ISO code when the default value changes,
   * and dispatches an action to reset the location suggestions in the Redux store.
   */
  useEffect(() => {
    if (!defaultValue) {
      setSearchItem('');
      setCountry(countryIso);
      dispatch(resetSuggestions());
    }
  }, [defaultValue, countryIso]);

  /**
   * Fetches location suggestions from the server based on the current search query
   * and user's country ISO code when the search query changes.
   */
  useEffect(() => {
    if (searchQuery) {
      dispatch(
        fetchLocations({
          searchItem: searchQuery,
          country: country.toLowerCase(),
        }),
      );
    }
  }, [searchQuery]);

  /**
   * Sets the location based on a selected suggestion from the list of location suggestions.
   * @param {Context} location - The context object for the selected location suggestion.
   */
  const handleSetLocation = (location: Context) => {
    const streetName = (
      location?.address?.street_name || location?.street?.name
    )
      ?.split(' ')
      .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
      .join(' ');

    const address = [
      streetName || '',
      location?.postcode?.name || '',
      location?.place?.name || '',
      location?.country?.name || '',
    ]
      .filter((item) => !!item)
      .join(' ');

    setSearchItem(address);
    setLocation(location);
    setShowList(false);
  };

  /**
   * Updates the current search query for location suggestions.
   * @param {string} value - The new value of the search query.
   */
  const onChange = (value: string) => {
    setShowList(true);
    setSearchItem(value);
  };

  return (
    <MapBoxStyled ref={ref}>
      <Input
        id={id}
        type="text"
        value={searchItem}
        onChange={(e) => onChange(e.target.value)}
        placeholder={'Ex. Lisbon, Av. dos Cravos Vermelhos, 2720-354'}
        label={label}
        showRequiredIcon={showRequiredIcon}
        autoComplete={'off'}
        errorMessage={errorMessage}
      />
      {showList ? (
        <section>
          {suggestions?.map((item) => (
            <LocationItem
              key={item.mapbox_id}
              onClick={() => {
                handleSetLocation(item.context);
              }}
            >
              <LocationIcon />
              <div>
                <h2>{item.name}</h2>
                <p>{item.place_formatted}</p>
              </div>
            </LocationItem>
          ))}
        </section>
      ) : (
        ''
      )}
    </MapBoxStyled>
  );
};

export default memo(MapBoxAutocompleteInput);
